import React, { memo, type ReactElement } from 'react';
import { SkeletonLoader } from '@saviynt/design-system';

import './SodListLoader.css';

/**
 * SodListLoader
 *
 * A skeleton loader component that displays a loading UI for SOD lists.
 * It renders a specified number of placeholder rows, each containing a leading icon,
 * center content, and trailing action, all animated with a pulse effect.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {number} props.count - The number of skeleton rows to display. Defaults to 3.
 *
 * @returns {JSX.Element} The rendered skeleton loader for SOD lists.
 *
 * @example
 * <SodListLoader count={5} />
 */
function SodListLoader({ count = 3 }: { count: number }): ReactElement {
  const array = Array.from(Array(count).keys());

  return (
    <div className='Sod-ListLoader'>
      {array.map((idx) => (
        <div key={idx} className='Sod-ListLoader-row'>
          <SkeletonLoader
            shape='square'
            animationStyle='wave'
            height='1.25rem'
            width='1.675rem'
            className='Sod-ListLoader-rowLeadingContent'
          />
          <div className='Sod-ListLoader-rowCenterContent'>
            <SkeletonLoader
              shape='square'
              animationStyle='wave'
              height='1rem'
              width='50%'
            />
            <SkeletonLoader
              shape='square'
              animationStyle='wave'
              height='2rem'
              width='100%'
            />
          </div>
          <div className='Sod-ListLoader-rowTrailingContent'>
            <SkeletonLoader
              shape='square'
              animationStyle='wave'
              height='2.25rem'
              width='5.75rem'
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default memo(SodListLoader);
